import { capitalizeFirstLetter } from 'state/helpers';


export function countInflightSales(invoices, chartType) {
    const objValues = invoices.reduce((acc, invoice) => {
        if (!acc.totalAmount) {
            acc.totalAmount = 0;
            acc.directAmount = 0;
            acc.preorderAmount = 0;
        }

        acc.totalAmount += (invoice['totalAmount'] || 0) / 1000000;
        acc.directAmount += (invoice['directAmount'] || 0)/ 1000000;
        acc.preorderAmount += (invoice['preorderAmount'] || 0)/ 1000000;
        
        return acc;
    }, {}); 
    
    if (Object.keys(objValues).length === 0) return [];
    
    
    const barChartData = [
        { x: 'Total', y: parseFloat((objValues.totalAmount).toFixed(2)) },
        { x: 'Direct', y: parseFloat((objValues.directAmount).toFixed(2)) },
        { x: 'Preorder', y: parseFloat((objValues.preorderAmount).toFixed(2)) }
    ];   

    switch (chartType) {
        case 'bar':
            return barChartData;
        case 'pie':
            const pieChartData = Object.entries({
                'Direct': parseFloat((objValues.directAmount).toFixed(2)),
                'Preorder': parseFloat((objValues.preorderAmount).toFixed(2))
            }).map(([key, value]) => ({
                id: key,
                label: key,
                value
            }));
            return pieChartData;
        default:
            return barChartData;            
    }
}

export function countAndAverageInvoicesByField(invoices, fieldName, groupBy, chartType) {
    if (!invoices || invoices.length === 0) return [];

    const objValues = invoices.reduce((acc, invoice) => {
        const key = invoice[groupBy];
        
        const value = invoice[fieldName] || 0;
        
        if (!acc[key]) {
            acc[key] = { count: 0, total: 0 };
        }
        acc[key].count += 1;
        acc[key].total += value / 1000000;
        
        return acc;
    }, {});

    if (Object.keys(objValues).length === 0) return [];

    const calculateAverage = (total, count) => (count > 0 ? parseFloat((total / count).toFixed(2)) : 0);

    const sortCriteria = (a, b) => 
        (fieldName === 'flightDate' 
        || fieldName === 'flightDateLocalBase'
        || fieldName === 'etd' 
        || fieldName === 'etdLocalBase')
            ? new Date(a.x) - new Date(b.x)  
            : a.x.localeCompare(b.x);

    // Convert accumulated data to the required format
    const arrValues = Object.entries(objValues)
        .map(([key, { count, total }]) => ({
            x: key,
            y: calculateAverage(total, count)
        }))
        .sort(sortCriteria);

    const totalCount = Object.values(objValues).reduce((acc, { count }) => acc + count, 0);
    const totalSum = Object.values(objValues).reduce((acc, { total }) => acc + total, 0);
    const overallAverage = calculateAverage(totalSum, totalCount);

    const arrAverage = arrValues.map(({ x }) => ({
        x,
        y: overallAverage,
    }));

    const barLineChartData = [
        {
            id: capitalizeFirstLetter(fieldName),
            color: 'hsl(120, 70%, 50%)',
            data: arrValues
        },
        {
            id: 'Average',
            color: 'hsl(200, 70%, 50%)',
            data: arrAverage
        },        
    ];

    switch (chartType) {
        case 'bar':
        case 'column':
            return arrValues;
        case 'pie':            
            return Object.entries(objValues).map(([key, total]) => ({
                id: key,
                label: key,
                value: total,
            }));
        case 'barline':
            return barLineChartData;
        default:
            return barLineChartData;            
    }
}

export function countInvoicesByFieldStackedBarChart(invoices, fieldName) {    
    if (!invoices || invoices.length === 0) return null;

    const objValues = invoices.reduce((acc, invoice) => {
        const key = invoice[fieldName];               
        
        if (!acc[key]) {
            acc[key] = { 
                totalAmount: 0, 
                directAmount: 0, 
                preorderAmount: 0 
            };
        }

        acc[key].totalAmount += (invoice['totalAmount'] || 0) / 1000000;
        acc[key].directAmount += (invoice['directAmount'] || 0) / 1000000;
        acc[key].preorderAmount += (invoice['preorderAmount'] || 0) / 1000000;
        
        return acc;
    }, {});

    if (Object.keys(objValues).length === 0) return [];
    
    const sortCriteria = (a, b) => 
        (fieldName === 'flightDate' 
        || fieldName === 'flightDateLocalBase'
        || fieldName === 'etd' 
        || fieldName === 'etdLocalBase')
            ? new Date(a.x) - new Date(b.x)  
            : a.x.localeCompare(b.x);

    const chartData = Object.entries(objValues)
        .map(([key, value]) => {
            const direct = parseFloat((value.directAmount ?? 0).toFixed(2));
            const preorder = parseFloat((value.preorderAmount ?? 0).toFixed(2));
            
            return ({
                x: key,
                Direct: direct,
                Preorder: preorder,
            })
        })
        .sort(sortCriteria);

    return chartData;
}

export function countAndTotalInvoicesByFieldAndDuration(invoices, fieldName, groupBy, duration, chartType) {    
    if (!invoices || invoices.length === 0) return [];

    const objValues = invoices.reduce((acc, invoice) => {
        const key = invoice[groupBy];
        
        const value = invoice[fieldName] || 0;
        
        if (!acc[key]) {
            acc[key] = { 
                count: 0, 
                total: 0,                 
            };
        }

        acc[key].count += 1;
        acc[key].total += value / 1000000;        
        
        return acc;
    }, {});

    if (Object.keys(objValues).length === 0) return [];

    const sortCriteria = (a, b) => 
        (fieldName === 'flightDate' 
        || fieldName === 'flightDateLocalBase'
        || fieldName === 'etd' 
        || fieldName === 'etdLocalBase')
            ? new Date(a.x) - new Date(b.x)  
            : a.x.localeCompare(b.x);

    // Convert accumulated data to the required format
    const arrValues = Object.entries(objValues)
        .map(([formattedKey, { total }]) => ({
            x: formattedKey,
            y: parseFloat(total.toFixed(2)),
        }))
        .sort(sortCriteria);

    const totalSum = Object.values(objValues).reduce((acc, { total }) => acc + total, 0);
    const average = arrValues.length > 0 ? parseFloat((totalSum / arrValues.length).toFixed(2)) : 0;

    const arrAverage = arrValues.map(({ x }) => ({
        x,
        y: average
    }));
    
    const barLineChartData = [
        {
            id: capitalizeFirstLetter(fieldName),
            color: 'hsl(120, 70%, 50%)',
            data: arrValues
        },
        {
            id: 'Average',
            color: 'hsl(200, 70%, 50%)',
            data: arrAverage
        },        
    ];

    switch (chartType) {
        case 'bar':
        case 'column':
            return arrValues;
        case 'pie':            
            return Object.entries(objValues).map(([formattedKey, total]) => ({
                id: formattedKey,
                label: formattedKey,
                value: total,
            }));
        case 'barline':
            return barLineChartData;
        default:
            return barLineChartData;            
    }
}